import { useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaDesktop, FaLaptop, FaShieldAlt, FaSync, FaPowerOff,
    FaHdd, FaBatteryThreeQuarters, FaCheckCircle, FaTimesCircle,
    FaExclamationTriangle, FaHeadset, FaNetworkWired, FaUser,
    FaMapMarkerAlt, FaClock, FaLock, FaSpinner,
    FaExternalLinkAlt
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Endpoint Diagnostics Tab for IT Action Items.
 * Shows the requester's device/endpoint data from Entra ID + Intune,
 * with controls for common remediation actions (sync, reboot, remote connect).
 */
const EndpointDiagnosticsTab = ({ endpoint, requesterEmail }) => {
    const { apiFetch } = useAuth();
    const [actionLoading, setActionLoading] = useState({});

    const setLoading = (key, val) => setActionLoading(prev => ({ ...prev, [key]: val }));

    const handleSync = useCallback(async () => {
        if (!endpoint?.deviceId) return;
        setLoading('sync', true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/devices/${endpoint.deviceId}/sync`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                toast.success('Device sync initiated successfully.');
            } else {
                toast.error(data.message || 'Sync failed.');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to sync device.');
        } finally {
            setLoading('sync', false);
        }
    }, [apiFetch, endpoint?.deviceId]);

    const handleReboot = useCallback(async () => {
        if (!endpoint?.deviceId) return;
        if (!window.confirm('Are you sure you want to reboot this device? The user will lose any unsaved work.')) return;
        setLoading('reboot', true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/devices/${endpoint.deviceId}/reboot`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                toast.success('Device reboot initiated.');
            } else {
                toast.error(data.message || 'Reboot failed.');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to reboot device.');
        } finally {
            setLoading('reboot', false);
        }
    }, [apiFetch, endpoint?.deviceId]);

    const handleRemoteConnect = useCallback(() => {
        if (!endpoint?.splashtopAvailable) return;
        const protocol = endpoint.splashtopUriProtocol || 'st-business://';
        const hostname = endpoint.deviceName || endpoint.lastKnownHostname;
        if (!hostname) {
            toast.warn('No device hostname available for remote connection.');
            return;
        }
        window.open(`${protocol}com.splashtop.business?host=${encodeURIComponent(hostname)}`, '_self');
        toast.info(`Launching Splashtop session to ${hostname}...`, { autoClose: 3000 });
    }, [endpoint]);

    const formatBytes = (bytes) => {
        if (!bytes) return 'N/A';
        const gb = bytes / (1024 * 1024 * 1024);
        return `${gb.toFixed(1)} GB`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleString();
    };

    const getComplianceBadge = (state) => {
        if (!state) return { icon: FaExclamationTriangle, color: '#718096', label: 'Unknown' };
        const s = state.toLowerCase();
        if (s === 'compliant') return { icon: FaCheckCircle, color: '#38a169', label: 'Compliant' };
        if (s === 'noncompliant') return { icon: FaTimesCircle, color: '#e53e3e', label: 'Non-Compliant' };
        return { icon: FaExclamationTriangle, color: '#dd6b20', label: state };
    };

    if (!endpoint) {
        return (
            <div className='diag-empty-state'>
                <FaDesktop size={32} />
                <p>No endpoint data available for this requester.</p>
            </div>
        );
    }

    if (!endpoint.found) {
        return (
            <div className='diag-empty-state'>
                <FaDesktop size={32} />
                <p>No Intune-managed device found for this user.</p>
                {endpoint.lastKnownHostname && (
                    <p className='diag-hint'>Last known hostname: <strong>{endpoint.lastKnownHostname}</strong></p>
                )}
            </div>
        );
    }

    const compliance = getComplianceBadge(endpoint.complianceState);
    // Assign to an uppercase identifier so React renders it as a component rather than
    // an HTML element (lowercase JSX tags are treated as native DOM elements).
    const ComplianceIcon = compliance.icon;
    const storageUsedPercent = endpoint.totalStorageSpaceInBytes && endpoint.freeStorageSpaceInBytes
        ? Math.round(((endpoint.totalStorageSpaceInBytes - endpoint.freeStorageSpaceInBytes) / endpoint.totalStorageSpaceInBytes) * 100)
        : null;

    return (
        <div className='diag-panel endpoint-diag-panel'>
            {/* Header with quick actions */}
            <div className='diag-header'>
                <div className='diag-header-info'>
                    <FaLaptop size={18} />
                    <div>
                        <h4>{endpoint.deviceName || 'Unknown Device'}</h4>
                        <span className='diag-subtitle'>
                            {endpoint.manufacturer} {endpoint.model}
                            {endpoint.serialNumber && ` · S/N: ${endpoint.serialNumber}`}
                        </span>
                    </div>
                </div>
                <div className='diag-quick-actions'>
                    {(requesterEmail || endpoint.deviceName) && (
                        <a
                            className='diag-action-btn'
                            href={`/it/endpoint-management?search=${encodeURIComponent(requesterEmail || endpoint.deviceName)}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            title='Open this device in the Endpoint Management page (new tab)'
                        >
                            <FaExternalLinkAlt size={13} />
                            Endpoint Management
                        </a>
                    )}
                    {endpoint.splashtopAvailable && (
                        <button
                            className='diag-action-btn diag-action-primary'
                            onClick={handleRemoteConnect}
                            title='Launch Splashtop remote session'
                        >
                            <FaHeadset size={14} />
                            Remote Support
                        </button>
                    )}
                    <button
                        className='diag-action-btn'
                        onClick={handleSync}
                        disabled={actionLoading.sync}
                        title='Trigger Intune device sync'
                    >
                        {actionLoading.sync ? <FaSpinner className='spin' size={14} /> : <FaSync size={14} />}
                        Sync
                    </button>
                    <button
                        className='diag-action-btn diag-action-danger'
                        onClick={handleReboot}
                        disabled={actionLoading.reboot}
                        title='Reboot this device remotely'
                    >
                        {actionLoading.reboot ? <FaSpinner className='spin' size={14} /> : <FaPowerOff size={14} />}
                        Reboot
                    </button>
                </div>
            </div>

            {/* Info Grid */}
            <div className='diag-grid'>
                {/* User Context */}
                <div className='diag-card'>
                    <div className='diag-card-title'><FaUser size={13} /> User Context</div>
                    <div className='diag-card-body'>
                        <div className='diag-row'>
                            <span className='diag-label'>Department</span>
                            <span className='diag-value'>{endpoint.department || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Job Title</span>
                            <span className='diag-value'>{endpoint.jobTitle || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'><FaMapMarkerAlt size={11} /> Office</span>
                            <span className='diag-value'>{endpoint.officeLocation || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Account</span>
                            <span className={`diag-badge ${endpoint.accountEnabled ? 'diag-badge-green' : 'diag-badge-red'}`}>
                                {endpoint.accountEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'><FaClock size={11} /> Last Sign-In</span>
                            <span className='diag-value diag-value-small'>{formatDate(endpoint.lastSignInDateTime)}</span>
                        </div>
                    </div>
                </div>

                {/* Device Info */}
                <div className='diag-card'>
                    <div className='diag-card-title'><FaDesktop size={13} /> Device</div>
                    <div className='diag-card-body'>
                        <div className='diag-row'>
                            <span className='diag-label'>OS</span>
                            <span className='diag-value'>{endpoint.operatingSystem} {endpoint.osVersion}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'><FaClock size={11} /> Last Sync</span>
                            <span className='diag-value diag-value-small'>{formatDate(endpoint.lastSyncDateTime)}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Enrolled</span>
                            <span className='diag-value diag-value-small'>{formatDate(endpoint.enrolledDateTime)}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Management</span>
                            <span className='diag-value'>{endpoint.managementState || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* Compliance & Security */}
                <div className='diag-card'>
                    <div className='diag-card-title'><FaShieldAlt size={13} /> Security</div>
                    <div className='diag-card-body'>
                        <div className='diag-row'>
                            <span className='diag-label'>Compliance</span>
                            <span className={`diag-badge`} style={{ color: compliance.color }}>
                                <ComplianceIcon size={12} /> {compliance.label}
                            </span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'><FaLock size={11} /> Encryption</span>
                            <span className={`diag-badge ${endpoint.isEncrypted ? 'diag-badge-green' : 'diag-badge-red'}`}>
                                {endpoint.isEncrypted ? 'Encrypted' : endpoint.isEncrypted === false ? 'Not Encrypted' : 'Unknown'}
                            </span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Defender</span>
                            <span className={`diag-badge ${endpoint.windowsProtectionEnabled ? 'diag-badge-green' : 'diag-badge-red'}`}>
                                {endpoint.windowsProtectionEnabled ? 'Active' : endpoint.windowsProtectionEnabled === false ? 'Inactive' : 'Unknown'}
                            </span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Threat Level</span>
                            <span className='diag-value'>{endpoint.deviceThreatLevel || 'None'}</span>
                        </div>
                    </div>
                </div>

                {/* Network */}
                <div className='diag-card'>
                    <div className='diag-card-title'><FaNetworkWired size={13} /> Network</div>
                    <div className='diag-card-body'>
                        <div className='diag-row'>
                            <span className='diag-label'>IP Address</span>
                            <span className='diag-value'>{endpoint.ipAddressV4 || endpoint.lastKnownIpAddress || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Wi-Fi MAC</span>
                            <span className='diag-value diag-value-mono'>{endpoint.wiFiMacAddress || 'N/A'}</span>
                        </div>
                        <div className='diag-row'>
                            <span className='diag-label'>Ethernet MAC</span>
                            <span className='diag-value diag-value-mono'>{endpoint.ethernetMacAddress || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* Storage & Battery */}
                <div className='diag-card'>
                    <div className='diag-card-title'><FaHdd size={13} /> Health</div>
                    <div className='diag-card-body'>
                        {storageUsedPercent !== null && (
                            <div className='diag-row'>
                                <span className='diag-label'>Storage</span>
                                <div className='diag-progress-wrap'>
                                    <div className='diag-progress-bar'>
                                        <div
                                            className={`diag-progress-fill ${storageUsedPercent > 90 ? 'diag-progress-danger' : storageUsedPercent > 75 ? 'diag-progress-warn' : ''}`}
                                            style={{ width: `${storageUsedPercent}%` }}
                                        />
                                    </div>
                                    <span className='diag-progress-label'>
                                        {formatBytes(endpoint.freeStorageSpaceInBytes)} free of {formatBytes(endpoint.totalStorageSpaceInBytes)}
                                    </span>
                                </div>
                            </div>
                        )}
                        {endpoint.batteryChargePercent != null && (
                            <div className='diag-row'>
                                <span className='diag-label'><FaBatteryThreeQuarters size={11} /> Battery</span>
                                <div className='diag-progress-wrap'>
                                    <div className='diag-progress-bar'>
                                        <div
                                            className={`diag-progress-fill ${endpoint.batteryChargePercent < 20 ? 'diag-progress-danger' : ''}`}
                                            style={{ width: `${endpoint.batteryChargePercent}%` }}
                                        />
                                    </div>
                                    <span className='diag-progress-label'>
                                        {endpoint.batteryChargePercent}%
                                        {endpoint.batteryChargeCycles != null && ` · ${endpoint.batteryChargeCycles} cycles`}
                                    </span>
                                </div>
                            </div>
                        )}
                        {storageUsedPercent === null && endpoint.batteryChargePercent == null && (
                            <div className='diag-empty-inline'>Health data not available.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EndpointDiagnosticsTab;
